import React, { useState } from "react";
import { IoCloseCircleOutline } from "react-icons/io5";
import productCategory from "../../helpers/productCategory";
import { FaCloudUploadAlt } from "react-icons/fa";
import uploadImage from "../../helpers/uploadImage";
import DisplayImage from "./DisplayImage";
import { MdOutlineDeleteForever } from "react-icons/md";
import { summaryApi } from "../../common";
import { toast } from "react-toastify";
import { useTheme } from '../../components/Theme/ThemeProvider';
import { Cpu, HardDrive, Monitor, Palette, Smartphone, Star } from "lucide-react";
import { authFetch } from '../../helpers/authFetch';

function EditProducts({
    onClose,
    productData,
    fetchData
}) {
    const { colors } = useTheme();
    const [data, setData] = useState({
        ...productData,
        productName: productData?.productName || '',
        productImage: productData?.productImage || [],
        productBrand: productData?.productBrand || '',
        productPrice: productData?.productPrice || '',
        productsCategory: productData?.productsCategory || '',
        productDiscount: productData?.productDiscount || '',
        productDescription: productData?.productDescription || '' ,
        storage: productData?.storage || '',
        color: productData?.color || '',
        ram: productData?.ram || '',
        processor: productData?.processor || '',
        screenSize: productData?.screenSize || '',
        featured: productData?.featured || false   
    });

    const [uploadProductImageInput, setUploadProductImageInput] = useState("");
    const [fullScreenImage, setFullScreenImage] = useState("")
    const [openFullScreenImage, setOpenFullScreenImage] = useState(false)
    const [imagePreview, setImagePreview] = useState("");
    const [isUploading, setIsUploading] = useState(false);

    const handleOnChange = (e) => {
        const { name, value } = e.target
        setData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setData((prev) => ({
        ...prev,
        [name]: checked,
    }));
};

    const handleUploadProduct = async (e) => {
        try {
            const file = e.target.files[0];
            if (!file) return;

            setIsUploading(true);
            setUploadProductImageInput(file.name);

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);

            const uploadResult = await uploadImage(file);
            setData((prev) => ({
                ...prev,
                productImage: [...prev.productImage, uploadResult.secure_url],
            }));
            
            setTimeout(() => setImagePreview(""), 2000);
        } catch (error) {
            toast.error("Failed to upload image. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteProductImage = (index) => {
        const confirmDelete = window.confirm(
            "Are you sure you want to delete this image?"
        );

        if (!confirmDelete) return;

        const newProductImage = [...data.productImage];
        newProductImage.splice(index, 1);
        
        setData((prev) => ({
            ...prev,
            productImage: [...newProductImage],
        }));

        toast.success("Image removed successfully");
    }

    const handleSubmit = async(e)=>{
        e.preventDefault();

        if (data.productImage.length === 0) {
            toast.error("Please upload at least one product image");
            return;
        }

        try {
            const response = await authFetch(`${summaryApi.updateProduct.url}/${productData._id}`,{
                method: summaryApi.updateProduct.method,
               
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    productName: data?.productName,
                    productImage: data.productImage,
                    productBrand: data.productBrand,
                    productPrice: data.productPrice,
                    productsCategory: data.productsCategory,
                    productDiscount: data.productDiscount,
                    productDescription: data.productDescription,
                    storage: data.storage,
                    color: data.color,
                    ram: data.ram,
                    processor: data.processor,
                    screenSize: data.screenSize,
                    featured: data.featured
                })
            })
            const responseData = await response.json();

            if(responseData.success){
                toast.success(responseData?.message)
                onClose()
                fetchData()            
            }

            if (responseData.error) {
                toast.error(responseData?.message)            
            }
        } catch (error) {
            toast.error("Failed to update product");
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
            <div 
                className="rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto border"
                style={{
                    backgroundColor: colors.bgSecondary,
                    borderColor: colors.border,
                }}
            >
                {/* Header */}
                <div 
                    className="flex justify-between items-center p-6 sticky top-0 z-10 border-b"
                    style={{
                        backgroundColor: colors.bgSecondary,
                        borderColor: colors.border,
                        background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.accent}15 100%)`
                    }}
                >
                    <h2 className="text-2xl font-bold" style={{ color: colors.text }}>
                        Edit Product
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg transition-all hover:scale-110"
                        style={{ 
                            backgroundColor: colors.bg,
                            color: colors.primary
                        }}
                    >
                        <IoCloseCircleOutline className="text-2xl" />
                    </button>
                </div>

                <form className="grid gap-4 p-6" onSubmit={handleSubmit}>
                    {/* Product Name */}
                    <div>
                        <label htmlFor="productName" className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                            Product Name
                        </label>
                        <input
                            type="text"
                            id="productName"
                            placeholder="Enter Product Name"
                            value={data?.productName}
                            onChange={handleOnChange}
                            name="productName"
                            required
                            className="w-full px-4 py-3 rounded-lg outline-none focus:ring-2 transition-all"
                            style={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `2px solid ${colors.border}`,
                                boxShadow: 'var(--focused) ? `0 0 0 3px ${colors.primary}20` : none'
                            }}
                        />
                    </div>

                    {/* Product Image */}
                    <div>
                        <label className="block text-sm font-semibold mb-3" style={{ color: colors.text }}>
                            Product Images
                        </label>
                        <label htmlFor="uploadImageInput">
                            <div 
                                className="p-6 border-2 border-dashed rounded-lg h-32 w-full flex justify-center items-center cursor-pointer transition-all hover:scale-105"
                                style={{
                                    backgroundColor: colors.bg,
                                    borderColor: colors.primary,
                                }}
                            >
                                <div className="text-center flex justify-center items-center flex-col gap-2">
                                    <span className="text-4xl" style={{ color: colors.primary }}>
                                        <FaCloudUploadAlt />
                                    </span>
                                    <p className="text-sm font-medium" style={{ color: colors.text }}>
                                        {isUploading ? "Uploading..." : "Click to upload images"}
                                    </p>
                                    <input
                                        type="file"
                                        id="uploadImageInput"
                                        className="hidden"
                                        onChange={handleUploadProduct}
                                        accept="image/*"
                                        disabled={isUploading}
                                    />
                                </div>
                            </div>
                        </label>

                        {imagePreview && (
                            <div className="flex items-center gap-3 mt-3">
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="w-20 h-20 object-cover rounded-lg border"
                                    style={{ borderColor: colors.primary }}
                                />
                                <span className="text-sm font-semibold" style={{ color: colors.primary }}>
                                    ✓ Processing...
                                </span>
                            </div>
                        )}
                        
                        {data?.productImage[0] && (
                            <div className="flex items-center gap-3 flex-wrap mt-4">
                                {data.productImage.map((image, index) => (
                                    <div key={`${image}-${index}`} className="relative group">
                                        <img
                                            src={image}
                                            alt="Product"
                                            width={80}
                                            height={80}
                                            onClick={() => {
                                                setOpenFullScreenImage(true);
                                                setFullScreenImage(image);
                                            }}
                                            className="w-20 h-20 object-cover rounded-lg cursor-pointer transition-transform hover:scale-110 border"
                                            style={{ borderColor: colors.primary }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteProductImage(index)}
                                            className="absolute -top-2 -right-2 p-1 rounded-full transition-all hover:scale-110 hidden group-hover:block"
                                            style={{
                                                backgroundColor: colors.primary,
                                                color: 'white'
                                            }}
                                        >
                                            <MdOutlineDeleteForever size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Brand */}
                    <div>
                        <label htmlFor="productBrand" className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                            Product Brand
                        </label>
                        <input
                            type="text"
                            id="productBrand"
                            placeholder="Enter Product Brand"
                            value={data.productBrand}
                            name="productBrand"
                            onChange={handleOnChange}
                            required
                            className="w-full px-4 py-3 rounded-lg outline-none focus:ring-2 transition-all"
                            style={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `2px solid ${colors.border}`
                            }}
                        />
                    </div>

                    {/* Price and Discount */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="productPrice" className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                                Price
                            </label>
                            <input
                                type="number"
                                id="productPrice"
                                placeholder="0.00"
                                value={data.productPrice}
                                name="productPrice"
                                onChange={handleOnChange}
                                required
                                className="w-full px-4 py-3 rounded-lg outline-none focus:ring-2 transition-all"
                                style={{
                                    backgroundColor: colors.bg,
                                    color: colors.text,
                                    border: `2px solid ${colors.border}`
                                }}
                            />
                        </div>
                        <div>
                            <label htmlFor="productDiscount" className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                                Discount (%)
                            </label>
                            <input
                                type="number"
                                id="productDiscount"
                                placeholder="0"
                                value={data.productDiscount}
                                name="productDiscount"
                                onChange={handleOnChange}
                                required
                                className="w-full px-4 py-3 rounded-lg outline-none focus:ring-2 transition-all"
                                style={{
                                    backgroundColor: colors.bg,
                                    color: colors.text,
                                    border: `2px solid ${colors.border}`
                                }}
                            />
                        </div>
                    </div>

                    {/* Category */}
                    <div>
                        <label htmlFor="productsCategory" className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                            Category
                        </label>
                        <select
                            value={data.productsCategory}
                            onChange={handleOnChange}
                            id="productsCategory"
                            required
                            name="productsCategory"
                            className="w-full px-4 py-3 rounded-lg outline-none focus:ring-2 transition-all"
                            style={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `2px solid ${colors.border}`
                            }}
                        >
                            <option value="">Select Category</option>
                            {productCategory.map((item, index) => (
                                <option key={item.value + index} value={item.value}>
                                    {item.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div 
                        className="p-4 rounded-lg border-2"
                        style={{ 
                            borderColor: colors.primary,
                            backgroundColor: `${colors.primary}10`
                        }}
                    >
                        <h3 
                            className="text-lg font-bold mb-4 flex items-center gap-2"
                            style={{ color: colors.primary }}
                        >
                            <Smartphone className="w-5 h-5" />
                            Product Specifications
                        </h3>
                        
                        <div className="grid grid-cols-2 gap-4">
                            {/* Storage */}
                            <div>
                                <label 
                                    htmlFor="storage"
                                    className="text-sm font-semibold mb-2 flex items-center gap-2"
                                    style={{ color: colors.text }}
                                >
                                    <HardDrive className="w-4 h-4" />
                                    Storage
                                </label>
                                <input
                                    type="text"
                                    id="storage"
                                    placeholder="e.g., 128GB, 256GB"
                                    value={data.storage}
                                    name="storage"
                                    onChange={handleOnChange}
                                    className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                                    style={{
                                        backgroundColor: colors.bg,
                                        color: colors.text,
                                        border: `2px solid ${colors.border}`
                                    }}
                                />
                            </div>

                            {/* Color */}
                            <div>
                                <label 
                                    htmlFor="color"
                                    className="text-sm font-semibold mb-2 flex items-center gap-2"
                                    style={{ color: colors.text }}
                                >
                                    <Palette className="w-4 h-4" />
                                    Color
                                </label>
                                <input
                                    type="text"
                                    id="color"
                                    placeholder="e.g., Black, White"
                                    value={data.color}
                                    name="color"
                                    onChange={handleOnChange}
                                    className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                                    style={{
                                        backgroundColor: colors.bg,
                                        color: colors.text,
                                        border: `2px solid ${colors.border}`
                                    }}
                                />
                            </div>

                            {/* RAM */}
                            <div>
                                <label 
                                    htmlFor="ram"
                                    className="text-sm font-semibold mb-2 flex items-center gap-2"
                                    style={{ color: colors.text }}
                                >
                                    <Cpu className="w-4 h-4" />
                                    RAM
                                </label>
                                <input
                                    type="text"
                                    id="ram"
                                    placeholder="e.g., 8GB, 16GB"
                                    value={data.ram}
                                    name="ram"
                                    onChange={handleOnChange}
                                    className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                                    style={{
                                        backgroundColor: colors.bg,
                                        color: colors.text,
                                        border: `2px solid ${colors.border}`
                                    }}
                                />
                            </div>

                            {/* Processor */}
                            <div>
                                <label 
                                    htmlFor="processor"
                                    className="text-sm font-semibold mb-2 flex items-center gap-2"
                                    style={{ color: colors.text }}
                                >
                                    <Cpu className="w-4 h-4" />
                                    Processor
                                </label>
                                <input
                                    type="text"
                                    id="processor"
                                    placeholder="e.g., A17 Pro"
                                    value={data.processor}
                                    name="processor"
                                    onChange={handleOnChange}
                                    className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                                    style={{
                                        backgroundColor: colors.bg,
                                        color: colors.text,
                                        border: `2px solid ${colors.border}`
                                    }}
                                />
                            </div>

                            {/* Screen Size */}
                            <div className="col-span-2">
                                <label 
                                    htmlFor="screenSize"
                                    className=" text-sm font-semibold mb-2 flex items-center gap-2"
                                    style={{ color: colors.text }}
                                >
                                    <Monitor className="w-4 h-4" />
                                    Screen Size
                                </label>
                                <input
                                    type="text"
                                    id="screenSize"
                                    placeholder="e.g., 6.1 inches"
                                    value={data.screenSize}
                                    name="screenSize"
                                    onChange={handleOnChange}
                                    className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                                    style={{
                                        backgroundColor: colors.bg,
                                        color: colors.text,
                                        border: `2px solid ${colors.border}`
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Featured Product Checkbox */}
                    <div 
                        className="p-4 rounded-lg flex items-center gap-3"
                        style={{ 
                            backgroundColor: `${colors.accent}15`,
                            border: `2px solid ${colors.accent}`
                        }}
                    >
                        <input
                            type="checkbox"
                            id="featured"
                            name="featured"
                            checked={data.featured}
                            onChange={handleCheckboxChange}
                            className="w-5 h-5 rounded cursor-pointer"
                            style={{ accentColor: colors.accent }}
                        />
                        <label 
                            htmlFor="featured"
                            className="flex items-center gap-2 text-sm font-semibold cursor-pointer"
                            style={{ color: colors.text }}
                        >
                            <Star className="w-5 h-5" style={{ color: colors.accent }} />
                            Mark as Featured Product (Will appear on landing page)
                        </label>
                    </div>

                    {/* Description */}
                    <div>
                        <label htmlFor="productDescription" className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                            Description
                        </label>
                        <textarea
                            id="productDescription"
                            placeholder="Enter Product Description"
                            value={data.productDescription}
                            name="productDescription"
                            onChange={handleOnChange}
                            className="w-full px-4 py-3 rounded-lg outline-none focus:ring-2 transition-all resize-y min-h-[120px]"
                            style={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `2px solid ${colors.border}`
                            }}
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 mt-6">
                        <button
                            type="submit"
                            disabled={isUploading || data.productImage.length === 0}
                            className="flex-1 px-6 py-3 text-white font-semibold rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
                            style={{
                                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                            }}
                        >
                            {isUploading ? 'Uploading...' : 'Update Product'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 font-semibold rounded-lg transition-all hover:scale-105"
                            style={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `2px solid ${colors.border}`
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>

            {openFullScreenImage && (
                <DisplayImage
                    onClose={() => setOpenFullScreenImage(false)}
                    imgUrl={fullScreenImage}
                    name={uploadProductImageInput}
                />
            )}

            <style jsx>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
            `}</style>
        </div>
    )
}

export default EditProducts;
